import { format } from 'date-fns';
import type { ExportValueFormat } from '../types/dataController';

const EMPTY = '—';

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Accept unix ms, unix seconds (auto-scaled) or ISO strings; null when unparseable. */
export const toDate = (value: unknown, unit: 'ms' | 's' | 'iso' | 'auto' = 'auto'): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (unit === 'iso' || (unit === 'auto' && typeof value === 'string' && /[-:T]/.test(value))) {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const n = typeof value === 'string' ? Number(value) : value;
  if (!isFiniteNumber(n) || n <= 0) return null;
  let ms = n;
  if (unit === 's') ms = n * 1000;
  else if (unit === 'auto' && n < 1e11) ms = n * 1000; // seconds before the year 5138
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDateTime = (value: unknown, unit: 'ms' | 's' | 'iso' | 'auto' = 'auto'): string => {
  const d = toDate(value, unit);
  return d ? format(d, 'yyyy-MM-dd HH:mm:ss') : EMPTY;
};

export const formatBytes = (value: unknown): string => {
  if (!isFiniteNumber(value)) return EMPTY;
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = value / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

export const formatDurationSeconds = (value: unknown): string => {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!isFiniteNumber(n) || n < 0) return EMPTY;
  const total = Math.floor(n);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (!parts.length || s) parts.push(`${s}s`);
  return parts.join(' ');
};

export const formatJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? EMPTY;
  } catch {
    return String(value);
  }
};

/**
 * One formatter for every value the export prints (PDF, on-screen summary). Unknown objects
 * become compact JSON so nothing is silently dropped.
 */
export const formatExportValue = (value: unknown, fmt: ExportValueFormat = 'text'): string => {
  if (value === null || value === undefined) return EMPTY;
  switch (fmt) {
    case 'datetime-ms':
      return formatDateTime(value, 'ms');
    case 'datetime-s':
      return formatDateTime(value, 's');
    case 'datetime-iso':
      return formatDateTime(value, 'iso');
    case 'datetime':
      return formatDateTime(value, 'auto');
    case 'bool':
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (value === 1 || value === '1' || value === 'true') return 'Yes';
      if (value === 0 || value === '0' || value === 'false') return 'No';
      return String(value);
    case 'number':
      return isFiniteNumber(value) ? value.toLocaleString('en-AU') : String(value);
    case 'bytes':
      return formatBytes(value);
    case 'duration-s':
      return formatDurationSeconds(value);
    case 'json':
      return typeof value === 'string' ? value : formatJson(value);
    case 'mono':
    case 'text':
    default:
      if (typeof value === 'string') return value === '' ? EMPTY : value;
      if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
      if (Array.isArray(value)) {
        if (value.length === 0) return EMPTY;
        return value.every((v) => typeof v !== 'object' || v === null) ? value.map((v) => formatExportValue(v)).join(', ') : formatJson(value);
      }
      return formatJson(value);
  }
};

/** Strip the dashes: Redis and the Users API key most things by the compact form. */
export const compactUuid = (uuid: string): string => uuid.replace(/-/g, '').toLowerCase();

/** Add the dashes back (accepts either form). */
export const dashedUuid = (uuid: string): string => {
  const c = compactUuid(uuid);
  if (c.length !== 32) return uuid;
  return `${c.slice(0, 8)}-${c.slice(8, 12)}-${c.slice(12, 16)}-${c.slice(16, 20)}-${c.slice(20)}`;
};

export const isMinecraftUuid = (value: string): boolean => /^[0-9a-f]{32}$/i.test(compactUuid(value.trim()));
